// MessageStudentModal.js
// Teacher Home "Message" action: send a short reminder email to one student
// via POST /home/message-student (WP6, 2026-10-06 testing revisions).
import React, { useEffect, useRef, useState } from "react";
import {
  Modal,
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
} from "react-native";

import { FASTAPI_BASE_URL } from "../../../config";

const identity = (s) => s;

export const MESSAGE_MAX_CHARS = 1000;
export const SEND_TIMEOUT_MS = 10000;
const SUCCESS_CLOSE_DELAY_MS = 1200;

export const DEFAULT_REMINDER_KEY =
  "Hi {name}, we haven't seen you in a while — jump back in when you can. Your teacher";
const NO_EMAIL_KEY = "This student has no email address. Remind them in class.";
const RATE_LIMITED_KEY = "You've messaged this student recently. Please try again later.";
const SEND_FAILED_KEY = "The message could not be sent. Please try again.";
const TIMEOUT_KEY =
  "This is taking longer than expected. The message may still have been sent — check before trying again.";
const GENERIC_ERROR_KEY = "Something went wrong. Please try again.";

const ERROR_KEYS = {
  no_email: NO_EMAIL_KEY,
  rate_limited: RATE_LIMITED_KEY,
  send_failed: SEND_FAILED_KEY,
};

/** True for the placeholder addresses given to students without a real email. */
export const isNoEmailAddress = (email) => {
  const domain = String(email || "").split("@").pop() || "";
  return domain.toLowerCase().includes("noemail");
};

const displayName = (student) => {
  const name = String(student?.name || "").trim();
  return name || String(student?.email || "");
};

async function readErrorCode(res) {
  try {
    const body = await res.json();
    return body?.code || body?.detail?.code || null;
  } catch (e) {
    return null;
  }
}

export default function MessageStudentModal({
  visible,
  student,
  classId,
  fetchWithAuth,
  onClose,
  t = identity,
  isDarkMode = false,
}) {
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [sent, setSent] = useState(false);
  const closeTimer = useRef(null);

  const email = student?.email || "";
  const noEmail = isNoEmailAddress(email);

  // Reset + prefill every time the modal opens for a student.
  useEffect(() => {
    if (!visible) return undefined;
    setText(t(DEFAULT_REMINDER_KEY, { name: displayName(student) }));
    setError(noEmail ? t(NO_EMAIL_KEY) : null);
    setSending(false);
    setSent(false);
    // A stale success timer must not close a freshly opened modal.
    return () => clearTimeout(closeTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, email]);

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  const trimmed = text.trim();
  const canSend =
    !sending && !sent && !noEmail && trimmed.length >= 1 && trimmed.length <= MESSAGE_MAX_CHARS;

  const send = async () => {
    if (!canSend) return;
    setSending(true);
    setError(null);
    const controller = new AbortController();
    let timedOut = false;
    const timeout = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, SEND_TIMEOUT_MS);
    try {
      const res = await fetchWithAuth(`${FASTAPI_BASE_URL}/home/message-student`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ class_id: classId, student_email: email, message: trimmed }),
        signal: controller.signal,
      });
      if (res.ok) {
        setSent(true);
        closeTimer.current = setTimeout(() => onClose && onClose(), SUCCESS_CLOSE_DELAY_MS);
      } else {
        const code = await readErrorCode(res);
        setError(t(ERROR_KEYS[code] || GENERIC_ERROR_KEY));
      }
    } catch (e) {
      // Timeout (abort) is distinct: the server may still have sent the email.
      setError(t(timedOut ? TIMEOUT_KEY : GENERIC_ERROR_KEY));
    } finally {
      clearTimeout(timeout);
      setSending(false);
    }
  };

  const c = isDarkMode
    ? { bg: "#1e293b", text: "#f1f5f9", muted: "#94a3b8", border: "#334155", accent: "#818cf8", err: "#f87171", ok: "#34d399" }
    : { bg: "#ffffff", text: "#1f2937", muted: "#6b7280", border: "#d1d5db", accent: "#4f46e5", err: "#b91c1c", ok: "#047857" };

  return (
    <Modal visible={!!visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.backdrop}>
        <View style={[styles.card, { backgroundColor: c.bg, borderColor: c.border }]}>
          <Text style={[styles.title, { color: c.text }]}>{t("Message student")}</Text>
          <Text style={[styles.who, { color: c.text }]}>{displayName(student)}</Text>
          {email && displayName(student) !== email ? (
            <Text style={[styles.email, { color: c.muted }]}>{email}</Text>
          ) : null}

          <TextInput
            testID="message-student-input"
            style={[styles.input, { color: c.text, borderColor: c.border }]}
            multiline
            value={text}
            onChangeText={(v) => setText(v.slice(0, MESSAGE_MAX_CHARS))}
            editable={!sending && !sent && !noEmail}
            maxLength={MESSAGE_MAX_CHARS}
            placeholderTextColor={c.muted}
          />
          <Text testID="message-student-counter" style={[styles.counter, { color: c.muted }]}>
            {`${text.length} / ${MESSAGE_MAX_CHARS}`}
          </Text>

          {error ? (
            <Text testID="message-student-error" style={[styles.status, { color: c.err }]}>
              {error}
            </Text>
          ) : null}
          {sent ? (
            <Text testID="message-student-success" style={[styles.status, { color: c.ok }]}>
              {t("Message sent.")}
            </Text>
          ) : null}

          <View style={styles.actions}>
            <TouchableOpacity
              testID="message-student-cancel"
              onPress={onClose}
              disabled={sending}
              accessibilityState={{ disabled: sending }}
              style={[styles.btn, { borderColor: c.border, opacity: sending ? 0.5 : 1 }]}
            >
              <Text style={{ color: c.text }}>{t("Cancel")}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              testID="message-student-send"
              onPress={send}
              disabled={!canSend}
              accessibilityState={{ disabled: !canSend }}
              style={[styles.btn, styles.primary, { backgroundColor: c.accent, opacity: canSend ? 1 : 0.5 }]}
            >
              <Text style={styles.primaryText}>{sending ? t("Sending…") : t("Send")}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: { flex: 1, backgroundColor: "rgba(0,0,0,0.5)", alignItems: "center", justifyContent: "center", padding: 16 },
  card: { width: "100%", maxWidth: 480, borderRadius: 12, borderWidth: 1, padding: 20 },
  title: { fontSize: 18, fontWeight: "700", marginBottom: 8 },
  who: { fontSize: 15, fontWeight: "600" },
  email: { fontSize: 13, marginBottom: 8 },
  input: { minHeight: 120, borderWidth: 1, borderRadius: 8, padding: 10, marginTop: 8, textAlignVertical: "top" },
  counter: { fontSize: 12, textAlign: "right", marginTop: 4 },
  status: { fontSize: 13, marginTop: 8 },
  actions: { flexDirection: "row", justifyContent: "flex-end", marginTop: 16 },
  btn: { paddingVertical: 10, paddingHorizontal: 16, borderRadius: 8, borderWidth: 1, marginLeft: 8 },
  primary: { borderWidth: 0 },
  primaryText: { color: "#fff", fontWeight: "600" },
});
